/**
 * Kalkyle grid row identity + incoming-row merge.
 *
 * Delete must key pending rows by id *and* client draggableId. Unsaved note
 * lines have no id yet; treating them as keep-orphans resurrected the line
 * after delete while notes (local-only) were already gone.
 *
 * Stale Autosave (already in-flight) must not PUT a deleted row back — strip
 * at persist time using the same key set.
 */

import { readOfferGroupId } from "./offer-calc-item-groups";

export function calcRowIdentity(row) {
  if (row?.id != null && row.id !== "") return `id:${row.id}`;
  if (row?.draggableId) return `client:${row.draggableId}`;
  return null;
}

export function sameRowOrder(a, b) {
  if (a == null || b == null || a === "" || b === "") return false;
  return Number(a) === Number(b);
}

export function pendingDeleteKeysFor(item) {
  const keys = [];
  if (!item || typeof item !== "object") return keys;
  if (item.id != null && item.id !== "") {
    keys.push(`id:${item.id}`);
    keys.push(item.id);
    keys.push(String(item.id));
  }
  if (item.draggableId) keys.push(`client:${item.draggableId}`);
  return keys;
}

export function isPendingDeletedRow(pendingDeletes, row) {
  if (!pendingDeletes || pendingDeletes.size === 0 || !row) return false;
  const identity = calcRowIdentity(row);
  if (identity && pendingDeletes.has(identity)) return true;
  if (row.id != null && row.id !== "") {
    if (pendingDeletes.has(row.id) || pendingDeletes.has(String(row.id))) {
      return true;
    }
  }
  if (row.draggableId && pendingDeletes.has(`client:${row.draggableId}`)) {
    return true;
  }
  return false;
}

/**
 * Merge server/Formik rows into local grid rows without resurrecting a
 * just-deleted line (saved or unsaved).
 *
 * @param {object[]} localItems
 * @param {object[]} incomingOrdered already ordered incoming rows
 * @param {Set} pendingDeletes
 * @returns {object[]}
 */
export function mergeIncomingCalcRows(
  localItems,
  incomingOrdered,
  pendingDeletes
) {
  const ordered = (incomingOrdered || []).filter(
    (row) => !isPendingDeletedRow(pendingDeletes, row)
  );
  const local = (localItems || []).filter(
    (item) => !isPendingDeletedRow(pendingDeletes, item)
  );
  const localUnsaved = local.filter((item) => item?.id == null || item.id === "");
  if (!localUnsaved.length) return ordered;

  // Server caught up (same or more rows) — trust incoming; drop client-only
  // clones that would otherwise be re-POSTed as new creates.
  if (ordered.length >= local.length) return ordered;

  const incomingKeys = new Set(ordered.map(calcRowIdentity).filter(Boolean));
  const orphanedUnsaved = localUnsaved.filter((item) => {
    const key = calcRowIdentity(item);
    return key && !incomingKeys.has(key);
  });
  if (!orphanedUnsaved.length) return ordered;
  return [...ordered, ...orphanedUnsaved];
}

/**
 * Explicit «Bruk server»: clear grid tombstones and trust incoming rows.
 * Soft-sync must keep mergeIncomingCalcRows; this is only for conflict adopt.
 *
 * @param {object[]} incomingRows
 * @param {Set} pendingDeletes mutated — cleared
 * @returns {object[]} shallow-copied incoming rows (or [])
 */
export function adoptPristineServerGridRows(incomingRows, pendingDeletes) {
  if (pendingDeletes && typeof pendingDeletes.clear === "function") {
    pendingDeletes.clear();
  }
  return Array.isArray(incomingRows)
    ? incomingRows.map((row) =>
        row && typeof row === "object" ? { ...row } : row
      )
    : [];
}

/** Rows present in previous but missing from next — persist as delete: true. */
export function deletedItemTombstones(previousItems, nextItems) {
  const nextIds = new Set(
    (nextItems || [])
      .filter((row) => row?.id != null && row.id !== "")
      .map((row) => String(row.id))
  );
  return (previousItems || [])
    .filter((row) => row?.id != null && row.id !== "")
    .filter((row) => !nextIds.has(String(row.id)))
    .map((row) => ({ id: row.id, delete: true }));
}

/** All identity keys for rows that left the set (id + client draggableId). */
export function deletedRowKeys(previousItems, nextItems) {
  const nextKeys = new Set(
    (nextItems || []).map(calcRowIdentity).filter(Boolean)
  );
  const keys = [];
  (previousItems || []).forEach((item) => {
    const identity = calcRowIdentity(item);
    if (!identity || nextKeys.has(identity)) return;
    pendingDeleteKeysFor(item).forEach((key) => keys.push(key));
  });
  return keys;
}

/**
 * Drop user-deleted rows from a calc payload (stale Autosave / GET).
 * Tombstones only rows that actually left THIS calc's item set (or were
 * already listed in `_deletedItems`). Never dump the whole shared session
 * delete Set — that leaked `delete: true` onto other calculations.
 */
export function stripDeletedCalcItems(calc, deletedKeys) {
  if (!calc) return calc;
  if (!deletedKeys || deletedKeys.size === 0) return calc;
  const previousItems = calc.items || [];
  const items = previousItems.filter(
    (row) => !isPendingDeletedRow(deletedKeys, row)
  );
  const removedNestedIds = new Set();
  const tombstones = [];
  const existing = calc._deletedItems || [];
  const seen = new Set(existing.map((row) => String(row.id)));
  previousItems.forEach((row) => {
    if (!isPendingDeletedRow(deletedKeys, row)) return;
    const nestedId = row?.nested_calculation_id ?? row?.nested_calculation?.id;
    if (nestedId != null) removedNestedIds.add(String(nestedId));
    if (row?.id == null || row.id === "") return;
    if (seen.has(String(row.id))) return;
    tombstones.push({ id: row.id, delete: true });
    seen.add(String(row.id));
  });
  const nested = (calc.nested_calculations || []).filter((child) => {
    if (child?.id == null) return true;
    return !removedNestedIds.has(String(child.id));
  });
  return {
    ...calc,
    items,
    nested_calculations: nested,
    _deletedItems: [...existing, ...tombstones],
  };
}

/**
 * After PUT: never keep a saved row the user deleted, including a recreated
 * id that replace-set invented for a stale client id.
 */
export function mergeSavedItemsAfterPersist({
  savedItems,
  sentItems,
  deletedKeys,
}) {
  const liveSent = (sentItems || []).filter((row) => row?.delete !== true);
  const sentIds = new Set(
    liveSent
      .filter((row) => row?.id != null && row.id !== "")
      .map((row) => String(row.id))
  );
  const sentUnsavedOrders = new Set(
    liveSent
      .filter((row) => row?.id == null || row.id === "")
      .map((row, index) =>
        row.row_order != null && row.row_order !== ""
          ? Number(row.row_order)
          : index
      )
  );
  return (savedItems || []).filter((row, index) => {
    if (isPendingDeletedRow(deletedKeys, row)) return false;
    const sid = row?.id != null && row.id !== "" ? String(row.id) : null;
    if (sid && sentIds.has(sid)) return true;
    if (!sid) return true;
    const order =
      row.row_order != null && row.row_order !== ""
        ? Number(row.row_order)
        : index;
    return sentUnsavedOrders.has(order);
  });
}

/**
 * Soft-sync / placement hold: never append server-only rows (just-deleted).
 * Keep local membership; copy server fields onto matching ids.
 */
export function preferLocalItemSet(localItems, serverItems) {
  const local = localItems || [];
  const serverById = new Map();
  (serverItems || []).forEach((row) => {
    if (row?.id != null && row.id !== "") serverById.set(String(row.id), row);
  });
  return local.map((localRow) => {
    if (localRow?.id == null || localRow.id === "") return localRow;
    const serverRow = serverById.get(String(localRow.id));
    if (!serverRow) return localRow;
    return {
      ...serverRow,
      draggableId: localRow.draggableId || serverRow.draggableId,
      offer_group_id: readOfferGroupId(localRow),
    };
  });
}
